import React, { useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { setSetting } from "actions";

import SortButton from "components/Button/SortButton";
import { Button } from "components/Form";
import IdConnectedList from "components/IdConnectedList";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import MessageBox from "components/MessageBox";
import SearchInputTable from "components/SearchInput";
import GlobalSearch from "components/SearchInput/GlobalSearch";

import { GlobalSearchFields } from "constants/globalSearch";
import { ApiModel } from "constants/loading";
import { Settings } from "constants/settings";

import { openImportAuctionsPlusCSV } from "lib/navigation";

import {
  getCurrentSale,
  getSetting,
  selectAlphabeticalBuyerIds,
} from "selectors";

import { BuyerCard } from "./BuyerCard";
import { SortFields, type SortField } from "./buyerSortPolicy";
import { useSortedBuyerIds } from "./useSortedBuyerIds";

const GlobalSearchFilterContainer = styled.div`
  flex: 2 1 260px;
  min-width: 220px;

  // Wraps to its own line below, ahead of the search box and sort control in
  // DOM order, so those two - both order 0 - stay together on the first line.
  @media (max-width: ${({ theme }) => theme.breakpoints[0]}px) {
    order: 1;
  }
`;

const FilterRow = styled(Row)(
  ({ theme }) => `
  gap: ${theme.space[2]}px;
`,
);

// SearchInputTable doesn't forward className, so it can't be widened via styled(); pass this via its `style` prop instead.
const SEARCH_BOX_STYLE = { flex: "1 1 180px" };

// GlobalSearch also offers sale-lot-level fields (Auction Pen, Vendor, Label, etc.)
// that have no effect on the buyer cards shown here - only show fields this screen
// actually filters on.
const BUYERS_GLOBAL_SEARCH_FIELDS = [
  GlobalSearchFields.Buyer,
  GlobalSearchFields.BuyerStatus,
  GlobalSearchFields.PaymentMethod,
];

const BUYER_NAME_ASC_SORT_OPTION = {
  label: "Buyer (A–Z)",
  value: SortFields.BUYER_NAME_ASC,
};
const BUYER_NAME_DESC_SORT_OPTION = {
  label: "Buyer (Z–A)",
  value: SortFields.BUYER_NAME_DESC,
};
const BIDDER_NUMBER_ASC_SORT_OPTION = {
  label: "Bidder Number (1-99)",
  value: SortFields.BIDDER_NUMBER_ASC,
};
const BIDDER_NUMBER_DESC_SORT_OPTION = {
  label: "Bidder Number (99-1)",
  value: SortFields.BIDDER_NUMBER_DESC,
};
const STATUS_UNPAID_FIRST_SORT_OPTION = {
  label: "Status (Unpaid First)",
  value: SortFields.STATUS_UNPAID_FIRST,
};
const STATUS_UNPAID_LAST_SORT_OPTION = {
  label: "Status (Paid First)",
  value: SortFields.STATUS_UNPAID_LAST,
};

function EmptyBuyers() {
  return <MessageBox>No Lots sold yet.</MessageBox>;
}

function NoMatchingBuyers() {
  return <MessageBox>No buyers match the current filters.</MessageBox>;
}

export const BuyersComponent = () => {
  const sale = useSelector(getCurrentSale);
  const isUsingRegisteredBidders = !!sale?.using_registered_bidders;

  const buyerIds = useSelector(selectAlphabeticalBuyerIds);
  const [searchText, setSearchText] = useState("");

  const sortOptions = useMemo(
    () => [
      BUYER_NAME_ASC_SORT_OPTION,
      BUYER_NAME_DESC_SORT_OPTION,
      ...(isUsingRegisteredBidders
        ? [BIDDER_NUMBER_ASC_SORT_OPTION, BIDDER_NUMBER_DESC_SORT_OPTION]
        : []),
      STATUS_UNPAID_FIRST_SORT_OPTION,
      STATUS_UNPAID_LAST_SORT_OPTION,
    ],
    [isUsingRegisteredBidders],
  );

  const dispatch = useDispatch();
  const persistedSortBy = useSelector(
    getSetting(Settings.buyerSort) as (state: RootState) => SortField | null,
  );
  // Bidder sales are primarily worked from bidder number, so default to that; other sales default to buyer name.
  const [sortBy, setSortByState] = useState<SortField>(
    persistedSortBy ??
      (isUsingRegisteredBidders
        ? SortFields.BIDDER_NUMBER_ASC
        : SortFields.BUYER_NAME_ASC),
  );

  function setSortBy(newSortBy: SortField) {
    setSortByState(newSortBy);
    dispatch(setSetting(Settings.buyerSort, newSortBy));
  }

  const sortedIds = useSortedBuyerIds(
    searchText,
    sortBy,
    isUsingRegisteredBidders,
  );

  const searchPlaceholder = isUsingRegisteredBidders
    ? "Bidder number/Buyer"
    : "Quicksearch";

  return (
    <Column>
      <FilterRow padding={1} flexWrap alignCenter printHidden>
        <SearchInputTable
          handleChange={setSearchText}
          value={searchText}
          placeholder={searchPlaceholder}
          style={SEARCH_BOX_STYLE}
        />
        <GlobalSearchFilterContainer>
          <GlobalSearch allowedFields={BUYERS_GLOBAL_SEARCH_FIELDS} />
        </GlobalSearchFilterContainer>
        <SortButton
          value={sortBy}
          options={sortOptions}
          onChange={setSortBy}
          dataTour="buyerSortButton"
        />
      </FilterRow>
      <Row padding={1}>
        <Button
          data-tour="importAuctionsPlusResults"
          onClick={() => openImportAuctionsPlusCSV(sale)}
        >
          Import AuctionsPlus Results
        </Button>
      </Row>
      <IdConnectedList
        component={BuyerCard}
        orderedIds={sortedIds}
        emptyComponent={buyerIds.length ? NoMatchingBuyers : EmptyBuyers}
      />
    </Column>
  );
};

// Wait For Sync Wrapper
export const Buyers = () => {
  return (
    <WaitForSync
      requiredData={[
        ApiModel.SALE_LOTS,
        ApiModel.CONSIGNMENTS,
        ApiModel.BUSINESSES,
        ApiModel.BIDDERS,
      ]}
    >
      <BuyersComponent />
    </WaitForSync>
  );
};
